'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useStore } from './StoreContext';
import { employeeAPI, menuItemAPI, backAPI, storeAPI, shiftTemplateAPI, deductionsMasterAPI, companyAPI, payrollSettingAPI } from '../api/client';
import { unassignedBreakdown, isEmployeeListTruncated } from '../lib/onboardingAssignments';
import { getLocalDate } from '../utils/date';

/**
 * 初回オンボーディング進捗の単一ソース。
 *
 * ダッシュボードのチェックリスト・専用ページ・サイドバーのバッジが共有する。
 * ADMIN のときだけ取得し、_app に置いて画面遷移で unmount されないため 1 セッション 1 回で済む
 * (各画面は refresh() で明示的に再取得する)。進捗はクライアント側で各件数から算出 (DB カラムなし)。
 *
 * 設計: docs/04_機能仕様/初回オンボーディング仕様.md
 */

export interface OnboardingSignals {
  hasStore: boolean;
  // 🔴 席は必須。1 席も無いと来店受付が 422「選んだ席が店に登録されていません」で
  //    落ちる（`VisitController`）。必須から外れていた間は、チェックリストを全部終えた店が
  //    お客を 1 組も受け付けられなかった（2026-09-29 追加）。
  hasTable: boolean;         // 選択中店舗に座席が 1 つ以上
  // 🔴 会社のデフォルト時給を「決めた」か（2026-10-05・UX 監査 A-3）。新規会社は company_settings.base_hourly_wage が必ず NULL で始まり、
  //    従業員の登録画面にも時給の欄が無いので、決める入口がどこにも無く、開店初日の日払いで 0 円が出て初めて気づいていた。
  //    ⚠️ 0 円は「完全歩合と決めた」正当な値（WageResolver）。NULL（未決定）だけを未完了にし、0 は完了とする。
  hasWage: boolean;
  hasEmployee: boolean;      // オーナー本人 (ADMIN) を除く CAST/STAFF が 1 名以上
  // 🔴 **配属が無い従業員の数**（2026-10-18・本番で実測して追加）。
  //    `hasEmployee` は**行が 1 件あれば完了**なので、登録しただけで配属していない状態を通す。
  //    導入店のキャスト 7 人が配属ゼロで、伝票があるのにバックが 0 件のまま営業が始まっていた。
  //    ⚠️ **`hasEmployee` の意味は変えない**（完了済みのチェックリストを巻き戻すと
  //    運用中の会社にパネルが再表示される。UX 監査 A-3 の前科）。警告として別に出す。
  unassignedEmployees: number;
  // 一覧を取り切れなかったか（`unassignedEmployees` が過少な可能性がある）。
  employeesTruncated: boolean;
  // 🔴 うち**まだ招待を送っていない**人の数（＝**店側がやること**）。
  //    新規の店の既定状態はこれ。「受諾してもらってください」と出すと永久に本人を待つ。
  unassignedNotInvited: number;
  // うち**招待済みで未受諾**の人の数（＝**本人がやること**。期限切れなら再送）。
  unassignedInvitePending: number;
  // 配属が無い人の先頭の id（受諾済みだけ。配属モーダルを直接開く導線に使う）。
  firstUnassignedEmployeeId: number | null;
  // 招待を送っていない人の先頭の id（招待タブを直接開く導線に使う）。
  firstNotInvitedEmployeeId: number | null;
  hasMenu: boolean;
  hasBackPolicy: boolean;
  hasClosedDay: boolean;     // 選択中店舗で締め済み営業日あり
  hasShiftTemplate: boolean; // 以下おすすめ設定 (任意)
  hasSubmissionPolicy: boolean;
  hasDeduction: boolean;
}

interface OnboardingProgressValue {
  loading: boolean;
  ready: boolean;            // 一度でも取得完了したか
  signals: OnboardingSignals;
  requiredDone: number;
  requiredTotal: number;
  optionalDone: number;
  optionalTotal: number;
  allRequiredDone: boolean;
  requiredIncomplete: number; // サイドバーバッジ用 (必須の未完了数)
  refresh: () => void;
}

const REQUIRED_KEYS: (keyof OnboardingSignals)[] = ['hasStore', 'hasTable', 'hasWage', 'hasEmployee', 'hasMenu', 'hasBackPolicy', 'hasClosedDay'];
const OPTIONAL_KEYS: (keyof OnboardingSignals)[] = ['hasShiftTemplate', 'hasSubmissionPolicy', 'hasDeduction'];

const EMPTY: OnboardingSignals = {
  hasStore: false, hasTable: false, hasWage: false, hasEmployee: false, hasMenu: false, hasBackPolicy: false, hasClosedDay: false,
  unassignedEmployees: 0, unassignedNotInvited: 0, unassignedInvitePending: 0,
  employeesTruncated: false, firstUnassignedEmployeeId: null, firstNotInvitedEmployeeId: null,
  hasShiftTemplate: false, hasSubmissionPolicy: false, hasDeduction: false,
};

// 直近1年以内に締め済み営業日があれば「初回締め済み」とみなす。営業日は JST 基準。
function recentRange(): { date_from: string; date_to: string } {
  const from = new Date();
  from.setFullYear(from.getFullYear() - 1);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return { date_from: getLocalDate(from), date_to: getLocalDate(tomorrow) };
}

const OnboardingProgressContext = createContext<OnboardingProgressValue | undefined>(undefined);

export const OnboardingProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, roleInfo } = useAuth();
  const { selectedStore } = useStore();
  const isAdmin = !!roleInfo?.is_admin;

  const [signals, setSignals] = useState<OnboardingSignals>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user || !isAdmin) {
      setSignals(EMPTY);
      setReady(false);
      return;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        // 初回締めは選択中店舗の closed / locked 営業日で判定 (API は X-Store-ID ヘッダで scope)。
        // API の status は 1 値の完全一致なので絞らずに取る (closed だけだと日次締めした日が未完了に戻っていた)
        const closePromise = selectedStore?.id
          ? storeAPI.getStoreBusinessDays({ ...recentRange() }).catch(() => null)
          : Promise.resolve(null);

        // 席は選択中店舗に紐づくので、店舗が決まっていないときは取りに行かない
        const tablePromise = selectedStore?.id
          ? storeAPI.getStoreTables(selectedStore.id).catch(() => null)
          : Promise.resolve(null);

        const [storeRes, tableRes, empRes, menuRes, backRes, closeRes, shiftRes, settingsRes, dedRes, wageRes] = await Promise.allSettled([
          storeAPI.getStores({ include_unassigned: 1 }),
          tablePromise,
          // 🔴 **`per_page` を明示する**（2026-10-18・レビュー指摘）。既定は **15 件**（`EmployeeController@index`）なので、
          //    引数なしだと 16 人以上の会社で**配属が無い人を数え落とす**。しかも見えている人を配属すると
          //    **警告が消えて「全部終わった」ように見える**＝この警告が潰そうとした状態を再生産する。
          //    他の呼び出し元（`BackSettings.tsx`）も `per_page: 1000` を明示している。
          employeeAPI.getEmployees({ per_page: 1000 } as any),
          menuItemAPI.getMenuItems({} as any),
          backAPI.getPolicies({} as any),
          closePromise,
          shiftTemplateAPI.getShiftTemplates({ is_active: true } as any),
          companyAPI.getSettings(),
          deductionsMasterAPI.getAll(),
          payrollSettingAPI.getCompany(),
        ]);

        if (cancelled) return;

        const next: OnboardingSignals = { ...EMPTY };

        if (storeRes.status === 'fulfilled') {
          next.hasStore = (((storeRes.value as any)?.data ?? []) as any[]).length > 0;
        }
        if (tableRes.status === 'fulfilled' && tableRes.value) {
          // active でない席も「登録済み」と数える（受付は active を見ないため）
          next.hasTable = (((tableRes.value as any)?.data ?? []) as any[]).length > 0;
        }
        if (empRes.status === 'fulfilled') {
          const list = ((empRes.value as any)?.data ?? []) as any[];
          // オーナー (role_type=ADMIN, is_cast/is_staff=true のスーパーユーザー) は除外し主ロールで判定
          next.hasEmployee = list.some((e) => e?.role_type === 'CAST' || e?.role_type === 'STAFF');
          // 判定は `src/lib/onboardingAssignments.ts`（テストあり）が正。
          // ⚠️ ここに直接書かない（画面に書いた分岐はテストが当たらず壊れる。CLAUDE.md §5）。
          // ⚠️ `today` は**呼び側が渡す**（既定値を置くと未テストの経路ができる。lib の 🔴）。
          const br = unassignedBreakdown(list as any, getLocalDate());
          next.unassignedEmployees = br.all.length;
          // 🔴 **招待が未受諾の人は配属できない**（`user_id` が無いと FE も BE も弾く）。
          //    さらに「**まだ送っていない**（店側がやる）」と「**送ったが未受諾**（本人がやる）」で
          //    やる人が正反対なので、3 つに分けて持つ。
          next.unassignedNotInvited = br.notInvited.length;
          next.unassignedInvitePending = br.invitePending.length;
          // 配属モーダルを直接開くための id。**受諾済みの人だけ**（未受諾だと保存できないフォームが開く）。
          const firstId = Number(br.assignable[0]?.id);
          next.firstUnassignedEmployeeId = Number.isFinite(firstId) ? firstId : null;
          // 招待タブを直接開くための id（まだ送っていない人の先頭）。
          const firstInviteId = Number(br.notInvited[0]?.id);
          next.firstNotInvitedEmployeeId = Number.isFinite(firstInviteId) ? firstInviteId : null;
          // ⚠️ それでも取り切れていないなら件数は過少。0 を「全員配属済み」と読まないよう印を残す。
          next.employeesTruncated = isEmployeeListTruncated(
            (empRes.value as any)?.pagination?.total, list.length);
        }
        if (menuRes.status === 'fulfilled') {
          next.hasMenu = (((menuRes.value as any)?.data ?? []) as any[]).length > 0;
        }
        if (backRes.status === 'fulfilled') {
          const d = (backRes.value as any)?.data;
          const items = Array.isArray(d) ? d : (d?.data ?? []);
          next.hasBackPolicy = items.length > 0;
        }
        if (closeRes.status === 'fulfilled' && closeRes.value) {
          const days = ((closeRes.value as any)?.data?.business_days ?? []) as any[];
          next.hasClosedDay = days.some((d) => d?.status === 'closed' || d?.status === 'locked');
        }
        if (shiftRes.status === 'fulfilled') {
          const list = ((shiftRes.value as any)?.data ?? []) as any[];
          next.hasShiftTemplate = list.some((r) => !r.store_id); // 会社共通テンプレート
        }
        if (settingsRes.status === 'fulfilled') {
          const res = settingsRes.value as any;
          next.hasSubmissionPolicy = !!res?.success && res?.data != null && res.data.shift_submission_mode != null;
        }
        if (wageRes.status === 'fulfilled') {
          // NULL = 誰も決めていない。0 は完全歩合と決めた値なので完了にする（WageResolver の unset と同じ線引き）
          const w = (wageRes.value as any)?.base_hourly_wage;
          next.hasWage = w !== null && w !== undefined && w !== '';
        }
        if (dedRes.status === 'fulfilled') {
          next.hasDeduction = (((dedRes.value as any)?.data ?? []) as any[]).length > 0;
        }

        setSignals(next);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, isAdmin, selectedStore?.id, tick]);

  const requiredDone = REQUIRED_KEYS.filter((k) => signals[k]).length;
  const optionalDone = OPTIONAL_KEYS.filter((k) => signals[k]).length;

  const value: OnboardingProgressValue = {
    loading,
    ready,
    signals,
    requiredDone,
    requiredTotal: REQUIRED_KEYS.length,
    optionalDone,
    optionalTotal: OPTIONAL_KEYS.length,
    allRequiredDone: requiredDone === REQUIRED_KEYS.length,
    requiredIncomplete: REQUIRED_KEYS.length - requiredDone,
    refresh,
  };

  return (
    <OnboardingProgressContext.Provider value={value}>
      {children}
    </OnboardingProgressContext.Provider>
  );
};

export const useOnboardingProgress = (): OnboardingProgressValue => {
  const ctx = useContext(OnboardingProgressContext);
  if (!ctx) {
    throw new Error('useOnboardingProgress must be used within an OnboardingProgressProvider');
  }
  return ctx;
};
